/* eslint-disable arrow-body-style */
import i18n from '@i18next/i18n';
import Presentation from '@src/framework/docs-api/presentation';
import { EventType, SSEService } from '@src/slide/application/service/ai-ssistant/ai-generator/sse';
import { TEMPLATE_LIST } from '@src/slide/application/service/ai-ssistant/ai-generator/template-modal/constants';
import { EFontFamilyApplyPart } from '@src/slide/application/service/quick-tool-dialog-manager/core/quick-tool-dialog-manager.interface';
import { CLASSIC_THEME } from '@src/slide/application/service/theme-color-manager/theme-config';
import { IThemeSelectItem } from '@src/slide/domain/ui-model/element-modeles/Interface/theme-model.interface';
import restoreIcon from '@tencent/docs-design-resources/icon/mobile/svg/restore_line_regular_24.svg';
import loadingIcon from '@tencent/docs-design-resources/image/mobile/gif/ai_newlogo_72.gif';
import docIcon from '@tencent/docs-design-resources/mobile/svg/navigation_topic.svg';
import Button from '@tencent/dui-mobile/lib/components/Button';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import styles from './index.less';
import MarkdownCore from './markdown-renderer';
import { showModelToPay } from '@src/js/common/ark-extensions/dc-components/modal-to-pay';
import { ESpaceAidType, showDialogWithCode } from '@src/js/common/ark-extensions/dc-components/docs-component-size-limit-dialog';
import { ApiErrorCode, DialogType } from '@src/js/common/ark-extensions/dc-components/docs-component-size-limit-dialog/types';
import Snackbar from '@tencent/dui-mobile/lib/components/Snackbar';

interface ColorItemProps {
    colorSchema: IThemeSelectItem['colorSchema'];
    selected?: boolean;
    onSelect: () => void;
}

const ColorItem = ({ colorSchema, selected, onSelect: onClick }: ColorItemProps) => {
    return (
        <div
            className={classnames(styles.item, {
                [styles.selected]: selected,
            })}
            onClick={onClick}
        >
            <div className={styles.accent1} style={{ backgroundColor: `#${colorSchema.accent1.color}` }}>
                Aa
            </div>
            <div className={styles.otherAccentContainer}>
                <div className={styles.otherAccent} style={{ backgroundColor: `#${colorSchema.accent2.color}` }} />
                <div className={styles.otherAccent} style={{ backgroundColor: `#${colorSchema.accent3.color}` }} />
                <div className={styles.otherAccent} style={{ backgroundColor: `#${colorSchema.accent4.color}` }} />
                <div className={styles.otherAccent} style={{ backgroundColor: `#${colorSchema.accent5.color}` }} />
                <div className={styles.otherAccent} style={{ backgroundColor: `#${colorSchema.accent6.color}` }} />
            </div>
        </div>
    );
};

interface Value<T> {
    value: T;
    onChange: (value: T) => void;
}

const ColorPanel = (props: Value<string>) => {
    return (
        <div className={styles.gridContainer}>
            {CLASSIC_THEME.map(item => (
                <ColorItem
                    key={item.id}
                    colorSchema={item.colorSchema}
                    selected={props.value === item.id}
                    onSelect={() => props.onChange(item.id)}
                />
            ))}
        </div>
    );
};

interface FormComponentsProps<V> {
    onMount?: () => void;
    onConfirm(value: V): void;
}

let lastNoteContent = '';
export const Note = ({ onMount, onConfirm }: FormComponentsProps<string>) => {
    const domRef = useRef<HTMLDivElement | null>(null);
    const abortRef = useRef<(() => void) | null>(null);

    const [noteContent, setNoteContent] = useState(lastNoteContent);
    const [isGenerating, setIsGenerating] = useState(!noteContent);
    const [errorMsg, setErrorMsg] = useState('');

    const scroll = () => {
        const markdownDom = domRef.current;
        if (markdownDom && markdownDom.scrollTop < markdownDom.scrollHeight) {
            markdownDom.scrollTop = markdownDom.scrollHeight;
        }
    };

    useEffect(() => {
        onMount?.();
    }, []);

    const startGenerate = async () => {
        setNoteContent('');
        setIsGenerating(true);

        const outline = await Presentation.getInstance().extractSlideContent();
        if (outline.length <= 10) {
            setErrorMsg(i18n.t('文档内容过短，无法生成备注'));
            setIsGenerating(false);
            return;
        }

        const { abort } = SSEService.generateSpeech(outline, ({ type, data }) => {
            if (type === EventType.DONE) {
                setIsGenerating(false);
                abortRef.current = null;

                setNoteContent((text) => {
                    lastNoteContent = text;
                    return text;
                });
                scroll();
                return;
            }

            if (type === EventType.CONTENT) {
                const t = data?.content?.content?.text;
                if (!t) return;

                setNoteContent(text => text + t);
                scroll();
            }
        });

        abortRef.current = abort;
    };

    const createDoc = async () => {
        const result = await SSEService.createFile(noteContent);

        if (result.success) {
            location.href = result.fileUrl;
            return;
        }

        if (result.code === 411212) {
            showModelToPay({
                position: 'aisidebar',
                feature: 'aihelper',
                apprec: 'slide',
                name: i18n.t('演讲稿转文档'),
            });
        } else if (result.code === 411213) {
            showDialogWithCode(
                ApiErrorCode.SELF_FAIL,
                {
                    type: DialogType.SIZE_LIMIT_OWNER,
                },
                ESpaceAidType.CREATE_DOC_SLIDE,
            );
        } else {
            Snackbar.show({
                type: 'error',
                message: result.message ?? i18n.t('生成失败'),
            });
        }
    };

    useEffect(() => {
        if (!noteContent) startGenerate();

        return () => {
            abortRef.current?.();
        };
    }, []);

    return (
        <div className={styles.container}>
            <div className={styles.markdown} style={{ marginBottom: 16 }} ref={domRef}>
                {errorMsg ? <div className={styles.error}>{errorMsg}</div> : <MarkdownCore>{noteContent}</MarkdownCore>}
            </div>

            {!errorMsg && (isGenerating ? (
                <div className={styles.opArea}>
                    <img src={loadingIcon} className={styles.loadingIcon} />
                    <span className={styles.loadingText}>{i18n.t('AI文档助手创作中...')}</span>
                </div>
            ) : (
                <div className={styles.opArea}>
                    <div className={styles.opGroup}>
                        <div className={styles.iconBtn} onClick={startGenerate}>
                            <img src={restoreIcon} className={styles.iconImg} />
                            {i18n.t('重新生成')}
                        </div>
                        <div className={styles.iconBtn} onClick={createDoc}>
                            <img src={docIcon} className={styles.iconImg} />
                            {i18n.t('转为文档')}
                        </div>
                    </div>
                    <Button
                        type="primary"
                        className={styles.insertButton}
                        onClick={() => {
                            onConfirm(noteContent);
                            // 成功插入后清空
                            lastNoteContent = '';
                        }}
                    >
                        {/* <img src={tickIcon} className={styles.iconImg} /> */}
                        {i18n.t('插入备注')}
                    </Button>
                </div>
            ))}
        </div>
    );
};

export const Theme = ({ onMount, onConfirm }: FormComponentsProps<string>) => {
    const [themeId, setThemeId] = useState(CLASSIC_THEME[0].id);

    useEffect(() => {
        onMount?.();
    }, []);

    return (
        <div className={styles.container}>
            <ColorPanel value={themeId} onChange={setThemeId} />
            <Button
                type="primary"
                className={styles.comfirmButton}
                onClick={() => {
                    onConfirm(themeId);
                }}
            >
                {i18n.t('确定')}
            </Button>
        </div>
    );
};

const RangeOptions = [
    {
        label: i18n.t('标题'),
        value: EFontFamilyApplyPart.TITLE,
    },
    {
        label: i18n.t('正文'),
        value: EFontFamilyApplyPart.CONTENT,
    },
    {
        label: i18n.t('标题和正文'),
        value: EFontFamilyApplyPart.ALL,
    },
];

const FontOptions = [
    '默认字体',
    '雅黑',
    '宋体',
    '黑体',
    '冬青黑简体中文',
    '苹方',
    '华文仿宋',
    '华文楷体',
    '华文宋体',
].map(font => ({
    label: font,
    value: font,
}));

export const Font = ({ onMount, onConfirm }: FormComponentsProps<{ range: string; font: string; }>) => {
    const [range, setRange] = useState(RangeOptions[0].value);
    const [font, setFont] = useState(FontOptions[0].value);

    useEffect(() => {
        onMount?.();
    }, []);

    return (
        <div className={styles.container}>
            <div className={styles.radioContainer}>
                {RangeOptions.map(item => (
                    <div
                        key={item.value}
                        className={classnames(styles.radioItem, {
                            [styles.selected]: range === item.value,
                        })}
                        onClick={() => setRange(item.value)}
                    >
                        {item.label}
                    </div>
                ))}
            </div>
            <div className={styles.fontContainer}>
                {FontOptions.map(item => (
                    <div
                        key={item.value}
                        className={classnames(styles.fontItem, {
                            [styles.selected]: font === item.value,
                        })}
                        onClick={() => setFont(item.value)}
                    >
                        {item.label}
                    </div>
                ))}
            </div>

            <Button
                type="primary"
                className={styles.comfirmButton}
                onClick={() => {
                    onConfirm({ range, font });
                }}
            >
                {i18n.t('确定')}
            </Button>
        </div>
    );
};

const TemplatePanel = (props: Value<string>) => {
    return (
        <div className={styles.gridContainer}>
            {TEMPLATE_LIST.map(item => (
                <div
                    key={item.id}
                    className={classnames(styles.templateItem, {
                        [styles.selected]: item.id === props.value,
                    })}
                    onClick={() => props.onChange(item.id)}
                >
                    <img src={item.cover} />
                </div>
            ))}
        </div>
    );
};

export const Template = ({ onMount, onConfirm }: FormComponentsProps<string>) => {
    const [templateId, setTemplateId] = useState(TEMPLATE_LIST[0].id);

    useEffect(() => {
        onMount?.();
    }, []);

    return (
        <div className={styles.container}>
            <TemplatePanel value={templateId} onChange={setTemplateId} />
            <Button
                type="primary"
                className={styles.comfirmButton}
                onClick={() => {
                    onConfirm(templateId);
                }}
            >
                {i18n.t('确定')}
            </Button>
        </div>
    );
};
